import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Stack, TextInput, Text, Group } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { isEqual } from 'lodash';
import { Patient } from '@medplum/fhirtypes';
import FormActionButtons, {
  FormActionButtonsRef,
} from '@/components/FormActionButtons';
import { LoaderWithChildren } from '@/components/LoaderWithChildren';
import {
  BirthMeasurementsFormState,
  BirthMeasurementsData,
} from '../utils/medical-history.types';
import {
  createInitialBirthMeasurementsFormState,
  mapObservationsToFormState,
  validateBirthMeasurementsForm,
  hasAnyBirthMeasurement,
} from '../utils/birth-observations.utils';

interface BirthMeasurementsModalProps {
  opened: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  patient: Patient;
  existingData: BirthMeasurementsData;
  onSave: (formState: BirthMeasurementsFormState) => Promise<void>;
  loading?: boolean;
  readonly?: boolean;
}

export const BirthMeasurementsModal: FC<BirthMeasurementsModalProps> = ({
  opened,
  onClose,
  onSuccess,
  patient,
  existingData,
  onSave,
  loading = false,
  readonly = false,
}) => {
  const { t } = useTranslation(['patient-pages', 'common']);
  const formActionsRef = useRef<FormActionButtonsRef>(null);
  const baselineRef = useRef<BirthMeasurementsFormState | null>(null);

  const [formState, setFormState] = useState<BirthMeasurementsFormState>(
    createInitialBirthMeasurementsFormState()
  );
  const [saving, setSaving] = useState(false);
  const [baselineSet, setBaselineSet] = useState(false);

  // Initialize form state from existing data when modal opens
  useEffect(() => {
    if (opened) {
      const initialState = mapObservationsToFormState(existingData);
      setFormState(initialState);
      setBaselineSet(true);
    } else {
      baselineRef.current = null;
      setBaselineSet(false);
    }
  }, [opened, existingData]);

  // Capture baseline after form state is initialized
  useEffect(() => {
    if (opened && baselineSet) {
      baselineRef.current = { ...formState };
      setBaselineSet(false);
    }
  }, [baselineSet, formState, opened]);

  // Validation
  const validation = useMemo(
    () => validateBirthMeasurementsForm(formState),
    [formState]
  );

  const hasData = useMemo(() => hasAnyBirthMeasurement(formState), [formState]);
  const isFormValid = validation.isValid && hasData;

  // Change detection
  const hasUnsavedChanges = useMemo(() => {
    if (!baselineRef.current) {
      return false;
    }
    try {
      return !isEqual(formState, baselineRef.current);
    } catch {
      return true;
    }
  }, [formState]);

  const handleFieldChange = (
    field: keyof BirthMeasurementsFormState,
    value: string
  ) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value === '' ? null : Number(value),
    }));
  };

  const handleDiscardChanges = () => {
    if (baselineRef.current) {
      setFormState(baselineRef.current);
    } else {
      setFormState(createInitialBirthMeasurementsFormState());
    }
  };

  const handleSave = async () => {
    if (!isFormValid || saving) {
      return;
    }

    setSaving(true);
    try {
      await onSave(formState);

      showNotification({
        color: 'green',
        title: t('medicalHistory.birthMeasurements.saveSuccessTitle', {
          ns: 'patient-pages',
          defaultValue: 'Save successful',
        }),
        message: t('medicalHistory.birthMeasurements.saveSuccessMessage', {
          ns: 'patient-pages',
          defaultValue: 'Birth measurements saved successfully.',
        }),
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Failed to save birth measurements:', error);
      showNotification({
        color: 'red',
        title: t('medicalHistory.birthMeasurements.saveErrorTitle', {
          ns: 'patient-pages',
          defaultValue: 'Save error',
        }),
        message:
          error instanceof Error
            ? error.message
            : t('medicalHistory.birthMeasurements.saveErrorMessage', {
                ns: 'patient-pages',
                defaultValue: 'An error occurred. Please try again.',
              }),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={() => formActionsRef.current?.handleGuardedClose()}
      title={t('medicalHistory.birthMeasurements.modalTitle', {
        ns: 'patient-pages',
        defaultValue: 'Birth Measurements',
      })}
      size="lg"
      closeOnClickOutside={false}
      closeOnEscape={false}
    >
      <LoaderWithChildren isLoading={loading}>
        <Stack gap="md">
          {patient.birthDate && (
            <Text size="sm" c="dimmed">
              {t('medicalHistory.birthMeasurements.birthDateLabel', {
                ns: 'patient-pages',
                defaultValue: 'Birth date',
              })}
              : {new Date(patient.birthDate).toLocaleDateString('el-GR')}
            </Text>
          )}

          <Group grow wrap="nowrap">
            <TextInput
              name="weight"
              label={t('medicalHistory.birthMeasurements.weight', {
                ns: 'patient-pages',
              })}
              value={formState.birthWeight?.toString() ?? ''}
              onChange={(e) =>
                handleFieldChange('birthWeight', e.currentTarget.value)
              }
              error={validation.errors.birthWeight}
              disabled={readonly}
              rightSection={
                <Text size="sm" c="dimmed">
                  gr
                </Text>
              }
            />
            <TextInput
              name="height"
              label={t('medicalHistory.birthMeasurements.length', {
                ns: 'patient-pages',
              })}
              value={formState.birthLength?.toString() ?? ''}
              onChange={(e) =>
                handleFieldChange('birthLength', e.currentTarget.value)
              }
              error={validation.errors.birthLength}
              disabled={readonly}
              rightSection={
                <Text size="sm" c="dimmed">
                  cm
                </Text>
              }
            />
            <TextInput
              name="headCircumference"
              label={t('medicalHistory.birthMeasurements.headCircumference', {
                ns: 'patient-pages',
              })}
              value={formState.birthHeadCircumference?.toString() ?? ''}
              onChange={(e) =>
                handleFieldChange(
                  'birthHeadCircumference',
                  e.currentTarget.value
                )
              }
              error={validation.errors.birthHeadCircumference}
              disabled={readonly}
              rightSection={
                <Text size="sm" c="dimmed">
                  cm
                </Text>
              }
            />
          </Group>

          {!readonly && (
            <FormActionButtons
              ref={formActionsRef}
              onSave={handleSave}
              onDiscard={handleDiscardChanges}
              onClose={onClose}
              hasUnsavedChanges={hasUnsavedChanges}
              isLoading={saving}
              isFormValid={isFormValid}
              saveLabel={t('common:save', { defaultValue: 'Save' })}
              discardLabel={t('common:discard.label', {
                defaultValue: 'Discard changes',
              })}
            />
          )}
        </Stack>
      </LoaderWithChildren>
    </Modal>
  );
};

export default BirthMeasurementsModal;
